import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { Avatar, Spin } from "antd";
import AreaChart from "../../components/chart/AreaChart";
import ColumnBarChart from "../../components/chart/ColumnBarChart";
import { GetAdminDashboardApi } from "../../api/request/dashboard/dashboard";
import SkeletonTable from "../../components/table/SkeletonTable";
import recentFilesColumn from "../../columns/dashboard/recentFiles";
import UserAvatar from "../../components/common/UserAvatar";
import { getInitials } from "../../utils/utils";

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [taskData, setTaskData] = useState({
    categories: [],
    series: [],
  });
  const [recentFiles, setRecentFiles] = useState([]);

  const dashboardCards = [
    {
      title: "Unsubmitted Files",
      key: "unsubmitted_files",
      icon: "/assets/icons/pending-tickets.svg",
      bgColor: "bg-[#cfcced]",
      path: "/files_report",
      state: { status: "unsubmitted" },
    },
    {
      title: "In-Process Files",
      key: "in_process_files",
      icon: "/assets/icons/overdue-files.svg",
      bgColor: "bg-[#cdd8ec]",
      path: "/file",
    },
    {
      title: "Closed Files",
      key: "closed_files",
      icon: "/assets/icons/active-job-files.svg",
      bgColor: "bg-[#E5FAFF]",
      path: "/files_report",
      state: { status: "closed" },
    },
    {
      title: "Total Files",
      key: "total_files",
      icon: "/assets/icons/completed-files.svg",
      bgColor: "bg-[#DDF6E8]",
      path: "/files_report",
    },
  ];

  const totalClientData = {
    categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    series: [{ name: "Client", data: [12, 10, 28, 8, 19, 34, 50] }],
  };

  const signatureData = {
    categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"],
    series: [{ name: "Client", data: [12, 10, 28, 16, 31, 21, 38] }],
  };

  const getDashboard = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetAdminDashboardApi();
      if (data?.status) {
        setData(data?.data);
        const chartData = data.data.chart_data || [];
        const taskData = {
          categories: Array.isArray(chartData)
            ? chartData.map((item) => moment(item.date).format("MMM DD"))
            : [],
          series: [
            {
              name: "Closed",
              data: Array.isArray(chartData)
                ? chartData.map((item) => item.completed_tasks || 0)
                : [],
            },
            {
              name: "In-Process",
              data: Array.isArray(chartData)
                ? chartData.map((item) => item.running_tasks || 0)
                : [],
            },
          ],
        };
        setTaskData(taskData);
        setRecentFiles(
          data?.data?.latest_submitted_file?.map((item) => {
            return {
              ...item,
              task: item?.running_task?.pipeline_manage_task,
              userDetails: <UserAvatar data={item?.user} />,
              created_at: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "--",
            };
          })
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDashboard();
  }, []);

  const renderCard = (card, data) => (
    <div
      className="border rounded-xl p-4 bg-white cursor-pointer"
      onClick={() =>
        navigate(`${card?.path}/${data?.id}`, {
          state: { ...card?.state, service: data?.service_name },
        })
      }
    >
      <div className="flex justify-between items-center">
        <div>
          <p className="card-count mb-1">{data?.[card?.key] || 0}</p>
          <p className="font-medium">{card.title}</p>
        </div>
        <div
          className={`${card.bgColor} rounded-xl size-14 flex items-center justify-center`}
        >
          <img src={card.icon} alt="icon" />
        </div>
      </div>
    </div>
  );

  const formatActivityDate = (date) => {
    return moment(date)
      .fromNow()
      .replace("second", "sec")
      .replace("minute", "min");
  };

  const handleFileView = (data) => {
    navigate(`/file/${data?.service_type_id}/${data?.id}`, {
      state: { client: data?.user, task: data?.task },
    });
  };

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center flex-col mt-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : (
        <div>
          <div className="sm:space-y-5 space-y-4">
            {data?.services_count?.map((item) => (
              <div className="p-5 border rounded-xl bg-white">
                <h2 className="title mb-3">{item?.service_name}</h2>
                <div className="grid xl:grid-cols-4 md:grid-cols-2 gap-3">
                  {dashboardCards?.map((card) => renderCard(card, item))}
                </div>
              </div>
            ))}
          </div>

          <div className="grid xl:grid-cols-5 gap-5 sm:mt-5 mt-4">
            <div className="xl:col-span-3 col-span-2 border rounded-xl bg-white md:flex justify-center items-end flex-col overflow-x-auto">
              <div className="flex items-center justify-end gap-3 text-sm px-10 mb-3">
                <p>
                  <span className="text-xl text-secondary">●</span> Closed
                </p>
                <p>
                  <span className="text-xl text-Gray ">●</span> In-Process
                </p>
              </div>
              <div className="w-full">
                <ColumnBarChart
                  categories={taskData.categories}
                  series={taskData.series}
                />
              </div>
            </div>
            <div className="grid col-span-2 grid-cols-2 gap-3 sm:gap-5">
              <div
                className="bg-white col-span-2 sm:col-span-1 xl:col-span-1 grid border rounded-xl cursor-pointer overflow-hidden"
                onClick={() => navigate("/clients")}
              >
                <div className="px-4 pt-4">
                  <h4 className="mb-2">Total Client</h4>
                  <h2 className="card-count">{data?.total_clients || 0}</h2>
                </div>
                <AreaChart
                  data={totalClientData}
                  colors={["#7367F0"]}
                  height={80}
                />
              </div>
              <div
                className="bg-white col-span-2 sm:col-span-1 xl:col-span-1 grid border rounded-xl cursor-pointer overflow-hidden"
                onClick={() => navigate("/signature_report")}
              >
                <div className="px-4 pt-4">
                  <h4 className="mb-2">Signature</h4>
                  <h2 className="card-count">{data?.signature || 0}</h2>
                </div>

                <AreaChart
                  data={signatureData}
                  colors={["#03C3EC"]}
                  height={80}
                />
              </div>

              <div
                className="bg-white col-span-2 sm:col-span-1 xl:col-span-2 border rounded-xl cursor-pointer p-4 grid content-center gap-4"
                onClick={() => navigate("/payments_report")}
              >
                <div>
                  <h4 className="mb-3 title">Interac e-Transfer :</h4>
                  <hr className="mb-3" />
                  <div className="flex justify-around">
                    <div className="text-center">
                      <h4 className="card-count mb-1">
                        {data?.unpaid_interac_e_transfer_payments || 0}
                      </h4>
                      <p className="font-medium">Pending Request</p>
                    </div>
                    <div className="text-center">
                      <h4 className="card-count mb-1">
                        {data?.paid_interac_e_transfer_payments || 0}
                      </h4>
                      <p className="font-medium">Complete Request</p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="bg-white  col-span-2 sm:col-span-1 xl:col-span-2 border rounded-xl cursor-pointer p-4 grid content-center gap-4"
                onClick={() => navigate("/invoice_report")}
              >
                <div>
                  <h4 className="title mb-3">Invoices :</h4>
                  <hr className="mb-3" />
                  <div className="flex justify-around">
                    <div className="text-center">
                      <h4 className="card-count mb-1">
                        {data?.unpaid_invoice || 0}
                      </h4>
                      <p className="font-medium">Unpaid Invoice</p>
                    </div>
                    <div className="text-center">
                      <h4 className="card-count mb-1">
                        {data?.paid_invoice || 0}
                      </h4>
                      <p className="font-medium">Paid Invoice</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid xl:grid-cols-4 gap-5 mt-5">
            <div className="xl:col-span-1 lg:col-span-2 bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="title">Recent Activity</h3>
                <Link to="/activity" className="text-blue-500">
                  View All
                </Link>
              </div>

              <div className="h-[400px] overflow-y-scroll scrollbar-hide">
                <div className="sm:space-y-5 space-y-4">
                  {data?.latest_acitivty?.map((item) => (
                    <div className="flex items-center gap-3">
                      <div>
                        <Avatar
                          size="large"
                          className={`uppercase ${
                            !item?.user?.profile_image ? "bg-gray-400" : ""
                          }`}
                          src={item?.user?.profile_image}
                        >
                          {getInitials(item?.user?.name)}
                        </Avatar>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="sub-title">{item?.user?.name}</p>
                          <p className="text-xs text-Gray text-right">
                            {formatActivityDate(item?.created_at)}
                          </p>
                        </div>

                        <p className="text-Gray text-wrap w-full sm:max-w-full max-w-60">
                          {item?.activity_message}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="xl:col-span-3 lg:col-span-2 bg-white border rounded-xl p-4 overflow-x-auto">
              <div className="flex items-center justify-between sub-title mb-4">
                <h3 className="title">Recent File</h3>
                <Link to="/services" className="text-blue-500">
                  View All
                </Link>
              </div>

              <SkeletonTable
                columns={recentFilesColumn}
                data={recentFiles}
                isLoading={isLoading}
                onRowClick={(data) => handleFileView(data)}
                index={false}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
